import { useState, useEffect, useRef, FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Save,
  Eye,
  Mail,
  AlertCircle,
  Loader2,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import emailTemplateService from '@/services/email-template.service';
import {
  CreateEmailTemplateDto,
  UpdateEmailTemplateDto,
  TemplateVariable,
} from '@/types/email-template.types';
import SimpleSelect from '@/components/ui/SimpleSelect';
import VariablesPanel from '@/components/email-templates/VariablesPanel';
import DroppableInput from '@/components/email-templates/DroppableInput';
import RichTextEditor from '@/components/email-templates/RichTextEditor';
import CollapsiblePreview from '@/components/email-templates/CollapsiblePreview';
import { wrapWithReactEmailTemplate, extractVariables, replaceVariables, mergeAllowedVariables } from '@/utils/react-email-converter';
import { buildSectionSamples, placeSectionVariables } from '@/utils/email-section-placement';

export default function EmailTemplateEditorV1() {
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const isEditMode = !!id;

  // Fix: swap overflow-x-hidden → clip so sticky works on this page only
  useEffect(() => {
    const main = document.querySelector('main');
    if (!main) return;
    main.style.overflowX = 'clip';
    return () => { main.style.overflowX = ''; };
  }, []);

  // Form state — empty template_type in edit mode (loaded from API)
  const [formData, setFormData] = useState<CreateEmailTemplateDto>({
    template_key: '',
    template_name: '',
    template_type: isEditMode ? '' : 'WELCOME_EMAIL',
    subject_template: '',
    html_template: '',
    is_active: true
  });

  // UI state
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [showPreviewOverlay, setShowPreviewOverlay] = useState(false);

  // Ref for getting structure-preserved HTML from the editor at save time
  const cleanHtmlRef = useRef<(() => string) | null>(null);

  // Default email styles for React Email rendering
  const emailStyles = {
    primaryColor: '#3b82f6',
    backgroundColor: '#ffffff',
    textColor: '#374151',
    headingColor: '#111827',
    fontSize: 16,
    fontFamily: 'Arial, sans-serif'
  };

  // Fetch template for edit mode
  const { data: existingTemplate, isLoading: isLoadingTemplate } = useQuery({
    queryKey: ['emailTemplate', id],
    queryFn: () => emailTemplateService.getTemplateById(id!),
    enabled: isEditMode
  });

  // Fetch all available template types from API
  const { data: templateTypes = [] } = useQuery({
    queryKey: ['emailTemplateTypes'],
    queryFn: () => emailTemplateService.getTemplateTypes(),
    staleTime: 10 * 60 * 1000,
  });

  // Load existing template data
  useEffect(() => {
    if (existingTemplate) {
      setFormData({
        template_key: existingTemplate.template_key,
        template_name: existingTemplate.template_name,
        template_type: existingTemplate.template_type,
        subject_template: existingTemplate.subject_template,
        html_template: existingTemplate.html_template,
        is_active: existingTemplate.is_active
      });
    }
  }, [existingTemplate]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: CreateEmailTemplateDto) => emailTemplateService.createTemplate(data),
    onSuccess: () => {
      toast.success('Email template created successfully!');
      queryClient.invalidateQueries({ queryKey: ['emailTemplates'] });
      navigate('/admin/enterprise/email-templates');
    },
    onError: (error: any) => {
      const errorMessage = error?.message || 'Failed to create template';
      toast.error(errorMessage);
    }
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: UpdateEmailTemplateDto) => emailTemplateService.updateTemplate(id!, data),
    onSuccess: () => {
      toast.success('Email template updated successfully!');
      queryClient.invalidateQueries({ queryKey: ['emailTemplates'] });
      queryClient.invalidateQueries({ queryKey: ['emailTemplate', id] });
    },
    onError: (error: any) => {
      const errorMessage = error?.message || 'Failed to update template';
      toast.error(errorMessage);
    }
  });

  // Validation
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.template_key.trim()) {
      errors.template_key = 'Template key is required';
    } else if (!/^[a-zA-Z0-9_]+$/.test(formData.template_key)) {
      errors.template_key = 'Template key must be letters, numbers, and underscores only';
    }

    if (!formData.template_name.trim()) {
      errors.template_name = 'Template name is required';
    }

    if (!formData.subject_template.trim()) {
      errors.subject_template = 'Subject template is required';
    }

    if (!formData.html_template.trim()) {
      errors.html_template = 'HTML template is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handlers
  const handleInputChange = (field: keyof CreateEmailTemplateDto, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e?: FormEvent) => {
    if (e) e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix validation errors');
      return;
    }

    try {
      // In edit mode, use structure-preserving HTML from the editor
      // (patches user edits into the original DB template to avoid
      // contentEditable corrupting outer table layout / Handlebars blocks)
      const htmlToSave = (isEditMode && cleanHtmlRef.current)
        ? cleanHtmlRef.current()
        : formData.html_template;

      // Debug: verify structure-preserving save is working
      if (isEditMode) {
        const hasDoctype = htmlToSave.includes('<!DOCTYPE');
        const hasBodyTag = htmlToSave.includes('<body');
        console.log('[EmailSave]', {
          usingCleanHtml: !!cleanHtmlRef.current,
          hasDoctype,
          hasBodyTag,
          first200: htmlToSave.substring(0, 200),
        });
      }

      // Auto-generate allowed_variables from detected variables in templates
      const subjectVars = extractVariables(formData.subject_template || '');
      const htmlVars = extractVariables(htmlToSave || '');
      const allVariables = [...new Set([...subjectVars, ...htmlVars])];

      // Keep already-allowed variables (e.g. start_url) even if not used right now.
      const allowedVariables = mergeAllowedVariables(
        isEditMode ? (existingTemplate?.allowed_variables as Array<string | TemplateVariable> | undefined) : undefined,
        allVariables
      );

      if (isEditMode) {
        const updateData: UpdateEmailTemplateDto = {
          template_name: formData.template_name,
          template_type: formData.template_type,
          subject_template: formData.subject_template,
          html_template: htmlToSave,
          allowed_variables: allowedVariables,
          is_active: formData.is_active
        };
        updateMutation.mutate(updateData);
      } else {
        const createData = { ...formData, allowed_variables: allowedVariables };
        createMutation.mutate(createData);
      }
    } catch (error) {
      toast.error('Error preparing template data. Please check your template.');
    }
  };

  const handleCancel = () => {
    navigate('/admin/enterprise/email-templates');
  };

  // Generate sample data for preview - auto-detect variables from templates
  const generateSampleData = (): Record<string, any> => {
    try {
      const sampleData: Record<string, any> = {};

      // Extract variables from both subject and HTML templates
      const subjectVars = extractVariables(formData.subject_template || '');
      const htmlVars = extractVariables(formData.html_template || '');
      const allVariables = [...new Set([...subjectVars, ...htmlVars])];

      // Generate sample data for each detected variable
      allVariables.forEach(varName => {
        sampleData[varName] = `${varName.replace(/_/g, ' ')}`;
      });

      return sampleData;
    } catch (error) {
      console.error('Error generating sample data:', error);
      return {};
    }
  };

  const getPreviewHtml = (): string => {
    try {
      if (!formData.html_template) {
        return '<p style="color: #999; font-style: italic; padding: 20px;">Enter HTML template...</p>';
      }

      // In edit mode, use the structure-preserving patched HTML (full document)
      // so the preview matches exactly what will be saved to the DB and sent as email.
      // For new templates (create mode), use the body-only formData and wrap it.
      const isFullDocument = (html: string) => /<html[\s>]/i.test(html) || /<!DOCTYPE/i.test(html);

      let sourceHtml: string;
      if (isEditMode && cleanHtmlRef.current) {
        sourceHtml = cleanHtmlRef.current();
      } else {
        sourceHtml = formData.html_template;
      }

      // Section variables get a sample box, placed the same way the backend does.
      const sampleData = generateSampleData();
      const section_samples = buildSectionSamples(Object.keys(sampleData));
      let html = placeSectionVariables(sourceHtml, section_samples);
      html = replaceVariables(html, sampleData);

      // Replace any remaining unreplaced variables with placeholder text
      html = html.replace(/\{\{([^}]+)\}\}/g, (match) => {
        return `<span style="background-color: yellow; padding: 2px 4px;">${match}</span>`;
      });

      // Only wrap with React Email template if the content is NOT already a full HTML document.
      // Full-document templates (from DB) already have their own DOCTYPE, <html>, <head>, <body>,
      // and table layout — wrapping them again causes double-nesting and layout corruption.
      if (!isFullDocument(html)) {
        html = wrapWithReactEmailTemplate(html, emailStyles);
      }

      // Defence-in-depth sanitization (iframe sandbox="allow-same-origin" is the primary security boundary)
      html = html.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
      html = html.replace(/\son\w+\s*=\s*["'][^"']*["']/gi, '');
      html = html.replace(/\son\w+\s*=\s*[^\s>]+/gi, '');
      html = html.replace(/javascript\s*:/gi, '');
      if (html.includes('</head>')) {
        html = html.replace('</head>',
          '<style>a{pointer-events:none !important;cursor:default !important;}html,body{overflow:hidden !important;}</style></head>'
        );
      }

      return html;
    } catch (error) {
      console.error('Error generating preview:', error);
      return '<p style="color: red; padding: 20px;">Error generating preview. Please check your HTML template.</p>';
    }
  };

  const getPreviewSubject = (): string => {
    try {
      if (!formData.subject_template) return '';

      const sampleData = generateSampleData();
      let subject = replaceVariables(formData.subject_template, sampleData);

      // Replace any remaining unreplaced variables with placeholder text
      subject = subject.replace(/\{\{([^}]+)\}\}/g, (match) => {
        return `[${match}]`;
      });

      return subject;
    } catch (error) {
      console.error('Error generating subject preview:', error);
      return 'Error in subject template';
    }
  };

  if (isEditMode && isLoadingTemplate) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="bg-white -m-4 lg:-m-5 xl:-m-6">
        {/* Header - sticky at top */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 bg-white border-b">
          <div className="flex items-center gap-4">
            <button
              onClick={handleCancel}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">
                {isEditMode ? 'Edit Template' : 'New Template'} - {formData.template_name || 'Untitled'}
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setShowPreviewOverlay(true)}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              title="Preview Email"
            >
              <Eye className="w-4 h-4" />
              Preview
            </button>
            <button
              type="button"
              onClick={handleCancel}
              disabled={isSaving}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSaving}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {isEditMode ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {isEditMode ? 'Update' : 'Create'}
                </>
              )}
            </button>
          </div>
        </div>

        {/* 2-Column Layout */}
        <div className="flex">
          {/* Left Sidebar - sticky below header */}
          <div className="w-80 flex-shrink-0">
            <div className="sticky top-[57px]">
              <VariablesPanel templateType={formData.template_type} />
            </div>
          </div>

          {/* Right */}
          <div className="flex-1 bg-gray-50 min-w-0">
            <form onSubmit={handleSubmit} className="p-6 space-y-6 max-w-full">
              {/* Basic Information */}
              <div className="bg-white rounded-lg border p-6 space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>

                {/* Template Key */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Template Key <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.template_key}
                    onChange={(e) => handleInputChange('template_key', e.target.value)}
                    disabled={isEditMode}
                    placeholder="e.g., WELCOME_EMAIL_V1"
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      validationErrors.template_key ? 'border-red-500' : ''
                    } ${isEditMode ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                  />
                  {validationErrors.template_key && (
                    <p className="text-sm text-red-600 mt-1 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {validationErrors.template_key}
                    </p>
                  )}
                </div>

                {/* Template Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Template Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.template_name}
                    onChange={(e) => handleInputChange('template_name', e.target.value)}
                    placeholder="e.g., Welcome Email Template"
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      validationErrors.template_name ? 'border-red-500' : ''
                    }`}
                  />
                  {validationErrors.template_name && (
                    <p className="text-sm text-red-600 mt-1 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {validationErrors.template_name}
                    </p>
                  )}
                </div>

                {/* Template Type */}
                <SimpleSelect
                  label="Template Type"
                  required
                  value={formData.template_type}
                  onChange={(value) => handleInputChange('template_type', String(value))}
                  options={templateTypes.map(t => ({ label: t.label, value: t.value }))}
                  placeholder="Select Type"
                  isSearchable
                />
              </div>

              {/* Email Content */}
              <div className="bg-white rounded-lg border p-6 space-y-4 overflow-hidden">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Mail className="w-5 h-5 text-brand-purple-500" />
                  Email Content
                </h3>

                {/* Subject Template with Drag & Drop */}
                <DroppableInput
                  label="Subject"
                  value={formData.subject_template}
                  onChange={(value) => handleInputChange('subject_template', value)}
                  placeholder="e.g., Welcome to {{company_name}}, {{user_name}}!"
                  error={validationErrors.subject_template}
                  required
                  type="text"
                />

                {/* Rich Text Editor for Content */}
                <div className="w-full overflow-hidden">
                  <RichTextEditor
                    label="Content"
                    value={formData.html_template}
                    onChange={(value) => handleInputChange('html_template', value)}
                    placeholder="Enter email content here or drag variables..."
                    error={validationErrors.html_template}
                    required
                    minHeight="500px"
                    originalValue={existingTemplate?.html_template}
                    cleanHtmlRef={cleanHtmlRef}
                  />
                </div>
              </div>
            </form>
          </div>
        </div>

        {/* Preview Overlay Modal — lock background scroll */}
        {showPreviewOverlay && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-hidden"
            ref={(node) => {
              if (node) document.body.style.overflow = 'hidden';
              else document.body.style.overflow = '';
            }}
          >
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-4 border-b">
                <div className="flex items-center gap-2">
                  <Eye className="w-5 h-5 text-brand-green-500" />
                  <h3 className="text-lg font-semibold text-gray-900">Email Preview</h3>
                </div>
                <button
                  onClick={() => setShowPreviewOverlay(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Close Preview"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>

              {/* Modal Content — single scroll area */}
              <div className="flex-1 overflow-y-auto">
                <CollapsiblePreview
                  subjectPreview={getPreviewSubject()}
                  htmlPreview={getPreviewHtml()}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </DndProvider>
  );
}
